import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = checking, false = anon, object = logged in
    const [loading, setLoading] = useState(true);

    const fetchMe = useCallback(async () => {
        // Skip the network call if there's no auth cookie at all — keeps
        // public pages (landing, login, signup) free of 401 console noise.
        const hasCookie = typeof document !== "undefined" &&
            /(?:^|;\s*)access_token=/.test(document.cookie);
        const onAuthRoute = typeof window !== "undefined" &&
            ["/login", "/signup", "/"].includes(window.location.pathname);
        if (!hasCookie && onAuthRoute) {
            setUser(false);
            setLoading(false);
            return;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch {
            setUser(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchMe();
    }, [fetchMe]);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        setUser(data.user);
        return data.user;
    };

    const signup = async (payload) => {
        const { data } = await api.post("/auth/register", payload);
        setUser(data.user);
        // Return the full server response so callers can key off
        // onboarding_status when deciding where to route the user.
        return { ...data.user, onboarding_status: data.onboarding_status };
    };

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch {}
        setUser(false);
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, signup, logout, refresh: fetchMe }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}
